import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Minus, Plus, ShoppingCart, ShieldCheck, Truck, MessageCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { productImageUrls } from "@/components/ImageUploader";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addToCart } = useApp();
  const [p, setP] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setImgIdx(0);
      if (r.data.variants?.length) setVariantId(r.data.variants[0].id);
    });
  }, [id]);

  if (!p) return <div className="mx-auto max-w-7xl px-4 py-16 text-white/60">Loading...</div>;

  const variant = p.variants.find((v) => v.id === variantId) || p.variants[0];

  const buyOnWhatsApp = () => {
    if (!variant) return;
    const lineTotal = variant.price_inr * qty;
    const msg = [
      `*Hi Phone Experts — I'd like to buy this display*`,
      ``,
      `Product: ${p.title}`,
      `Brand / Model: ${p.brand} · ${p.model}`,
      `Variant: ${variant.name}${variant.sku ? ` (${variant.sku})` : ""}`,
      `Quantity: ${qty}`,
      `Price: ₹${variant.price_inr.toLocaleString("en-IN")} each`,
      `Total: ₹${lineTotal.toLocaleString("en-IN")} (free shipping)`,
      ``,
      `Please confirm availability and share payment/delivery details. Thanks!`,
    ].join("\n");
    window.open(`https://wa.me/917396871520?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  return (
    <div data-testid="product-detail" className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <button onClick={() => nav(-1)} className="mono-label text-white/50 hover:text-white mb-4">← Back</button>
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <div className="border border-white/10 bg-zinc-950 aspect-square overflow-hidden">
            <img src={productImageUrls(p)[imgIdx]} alt={p.title} className="w-full h-full object-cover"/>
          </div>
          {productImageUrls(p).length > 1 && (
            <div data-testid="pdp-thumbs" className="mt-3 flex gap-2 overflow-x-auto">
              {productImageUrls(p).map((u, i) => (
                <button
                  key={i}
                  data-testid={`pdp-thumb-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`shrink-0 h-16 w-16 border overflow-hidden transition-colors ${imgIdx === i ? "border-blue-500" : "border-white/10 hover:border-white/30"}`}
                >
                  <img src={u} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="mono-label text-blue-400 mb-2">{p.brand} · {p.model}</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">{p.title}</h1>

          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-mono text-3xl">{fmtINR(variant?.price_inr || 0)}</span>
            <span className="text-xs text-white/40">incl. all taxes · free shipping</span>
          </div>

          <div className="mb-6">
            <p className="mono-label text-white/50 mb-3">Choose variant</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {p.variants.map((v) => {
                const active = v.id === variantId;
                return (
                  <button
                    key={v.id}
                    data-testid={`variant-${v.id}`}
                    onClick={() => setVariantId(v.id)}
                    className={`text-left border p-4 transition-colors ${active ? "border-blue-500 bg-blue-600/10" : "border-white/10 bg-zinc-950 hover:border-white/30"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display">{v.name}</span>
                      <span className="font-mono text-sm">{fmtINR(v.price_inr)}</span>
                    </div>
                    <div className="text-xs text-white/50 mt-1 font-mono">
                      {v.sku} · {v.stock > 0 ? `${v.stock} in stock` : "Out of stock"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center border border-white/10">
              <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-white/5"><Minus className="h-4 w-4"/></button>
              <span data-testid="qty-value" className="px-5 font-mono">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-white/5"><Plus className="h-4 w-4"/></button>
            </div>
            <Button
              data-testid="add-to-cart-btn"
              size="lg"
              onClick={() => { addToCart(p, variant, qty); toast.success("Added to cart"); }}
              disabled={!variant || variant.stock === 0}
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-none"
            >
              <ShoppingCart className="h-5 w-5 mr-2"/> Add to cart
            </Button>
          </div>
          <Button
            data-testid="buy-now-btn"
            size="lg"
            onClick={buyOnWhatsApp}
            disabled={!variant || variant.stock === 0}
            className="w-full mb-6 bg-emerald-600 hover:bg-emerald-500 text-white rounded-none"
          >
            <MessageCircle className="h-5 w-5 mr-2"/> Buy now on WhatsApp
          </Button>

          <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 text-sm mb-6">
            <div className="bg-zinc-950 p-4 flex gap-3">
              <ShieldCheck className="h-5 w-5 text-blue-400 shrink-0"/>
              <div>
                <p className="font-medium">7-day warranty</p>
                <p className="text-white/50 text-xs">Manufacturing defects covered</p>
              </div>
            </div>
            <div className="bg-zinc-950 p-4 flex gap-3">
              <Truck className="h-5 w-5 text-blue-400 shrink-0"/>
              <div>
                <p className="font-medium">Free shipping</p>
                <p className="text-white/50 text-xs">Dispatched in 48 hours</p>
              </div>
            </div>
          </div>

          <div>
            <p className="mono-label text-white/50 mb-2">Description</p>
            <p className="text-white/80 leading-relaxed">{p.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
